import {
  bookingOnLoad,
  createBooking,
  createBookingEnc,
  requestSecSession,
  verifyInternalUser,
} from "api/services/Form";
import BookingAvailabilityNotice from "components/BookingAvailabilityNotice";
import { useError } from "context/ErrorContext";
import { useSecSession } from "context/SecSessionContext";
import moment from "moment-timezone";
import Disable from "pages/Disable";
import ErrorBoundary from "pages/ErrorBoundary/ErrorBoundary";
import Validation from "pages/Validation";
import { FC, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  constructSecurePayload,
  generateRandomString,
  getApiLangParam,
  getLocalizedValue,
  verifySignatureWithPKCS1,
} from "utils/helper";
import Layout from "../../../components/Layout";
import { PAGE } from "../../../entities";
import {
  BookingOnLoadRes,
  CareProvider,
  CreateBookingReqConfig,
  CreateBookingRes,
  Department,
  FORM_FLOW,
  FORM_SECTION,
  FormValues,
  InternalBookingAuthValues,
  RequestSecSessionRes,
  ServiceItem,
} from "../../../entities/Form";
import { AppointmentResult, RESULT } from "../../../entities/Result";
import {
  canContinueBookingWithNotice,
  getSystemMessage,
} from "../../../utils/bookingAvailability";
import Result from "../../Result";
import Form from "../Form";
import InternalValidation from "../InternalValidation";
import Preview from "../Preview";

interface HomePageProps {
  isInternalBooking?: boolean;
}

export const getServiceItemsWithProviders = (
  selectedDepartment?: Department,
) => {
  if (!selectedDepartment?.careProviders) return [];

  const serviceItemMap = new Map<
    string,
    { serviceItem: ServiceItem; careProviders: CareProvider[] }
  >();

  selectedDepartment.careProviders.forEach((careProvider) => {
    careProvider.serviceItems?.forEach((serviceItem) => {
      if (!serviceItemMap.has(serviceItem.value)) {
        serviceItemMap.set(serviceItem.value, {
          serviceItem,
          careProviders: [],
        });
      }
      serviceItemMap.get(serviceItem.value)?.careProviders.push(careProvider);
    });
  });

  return Array.from(serviceItemMap.values());
};

const defaultValues: FormValues = {
  departmentValue: undefined,
  serviceItemHid: undefined,
  careProviderHid: undefined,
  fromDt: "",
  lastName: "",
  firstName: "",
  idNo: "",
  countryCode: "852",
  mobileNo: "",
  email: "",
  isJoinedEhealth: "",
  remarks: "",
};

const HomePage: FC<HomePageProps> = ({ isInternalBooking = false }) => {
  const { i18n } = useTranslation();
  const navigate = useNavigate();
  const { secSession, setSecSession } = useSecSession();
  const { setError } = useError();
  const [searchParams] = useSearchParams();
  const cmsSiteId =
    searchParams.get("site") || searchParams.get("cmsSiteId") || "";
  const prevCmsSiteIdRef = useRef<string | null>(null);

  const [page, setPage] = useState(PAGE.FORM);
  const [formValues, setFormValues] = useState<FormValues>(defaultValues);
  const [activeFormSection, setActiveFormSection] = useState(
    FORM_SECTION.DEPARTMENT,
  );
  const [isFormValuesDirty, setIsFormValuesDirty] = useState(false);
  const [result, setResult] = useState<AppointmentResult>();
  const [patientFormNoScroll, setPatientFormNoScroll] = useState(false);
  const [scrollToMobileNo, setScrollToMobileNo] = useState(false);

  const [bookingOnLoadData, setBookingOnLoadData] =
    useState<BookingOnLoadRes>();
  const [bookingOnLoadDataError, setBookingOnLoadDataError] = useState(false);
  const systemMessage = getSystemMessage(bookingOnLoadData);
  const departmentOptions = useMemo(
    () =>
      bookingOnLoadData ? bookingOnLoadData.departmentOptions || [] : undefined,
    [bookingOnLoadData],
  );
  const showBookingAvailabilityNotice = canContinueBookingWithNotice(
    systemMessage,
    !!departmentOptions?.length,
  );
  const careProviderOptions = bookingOnLoadData
    ? departmentOptions?.find((opt) => opt.value === formValues.departmentValue)
        ?.careProviders || []
    : undefined;
  const serviceItemOptions = bookingOnLoadData
    ? careProviderOptions?.find(
        (opt) => opt.value === formValues.careProviderHid,
      )?.serviceItems || []
    : undefined;
  const flow = formValues.departmentValue
    ? departmentOptions?.find((opt) => opt.value === formValues.departmentValue)
        ?.flow
    : undefined;
  const [bookingTimeLabel, setBookingTimeLabel] = useState<string>();

  // Flow 2
  const serviceItemsWithProviders = useMemo(
    () =>
      getServiceItemsWithProviders(
        departmentOptions?.find(
          (opt) => opt.value === formValues.departmentValue,
        ),
      ),
    [departmentOptions, formValues.departmentValue],
  );

  const submit = (otpSid?: string, onError?: () => void) => {
    const {
      departmentValue,
      serviceItemHid,
      careProviderHid,
      fromDt,
      toDt,
      lastName,
      firstName,
      idNo,
      countryCode,
      mobileNo,
      email,
      isJoinedEhealth,
      remarks,
    } = formValues;
    const payload = {
      departmentHid: departmentOptions?.find(
        (dept) => dept.value === departmentValue,
      )?.hid,
      serviceItemHid,
      careProviderHid,
      fromDt,
      toDt,
      lastName,
      firstName,
      idNo,
      countryCode,
      mobileNo,
      email,
      isJoinedEhealth: isJoinedEhealth === "Y",
      remarks,
      idType: !!idNo ? "HKID" : undefined,
      otpSid,
    } as CreateBookingReqConfig;
    const onCreateBookingSuccess = (res: CreateBookingRes) => {
      const { bookingNo, isRequireOtp } = res;
      if (isRequireOtp) {
        if (otpSid) {
          onError?.();
          setError({
            message: i18n.t("systemErrorDetail"),
          });
        } else {
          setPage(PAGE.VALIDATION);
        }
      } else {
        const { enquiryPhoneNo, isConfirmed } = res;
        const locationName = getLocalizedValue({
          nameSc: res.locationNameSc,
          nameTc: res.locationNameTc,
          name: res.locationName,
        });

        const bookingTime = getLocalizedValue({
          nameSc: res.bookingTimeLabelSc,
          nameTc: res.bookingTimeLabelTc,
          name: res.bookingTimeLabel,
        });

        setPage(PAGE.RESULT);
        setResult(
          bookingNo
            ? {
                status: isConfirmed ? RESULT.SUCCESS : RESULT.PENDING,
                bookingNo,
                locationName,
                bookingTime,
                enquiryPhoneNo,
              }
            : {
                status: RESULT.FAIL,
                locationName,
                bookingTime,
                enquiryPhoneNo,
              },
        );
      }
    };

    if (bookingOnLoadData?.enc) {
      const onCreateBookingEnc = async (secSession: RequestSecSessionRes) =>
        createBookingEnc(
          await constructSecurePayload(
            { lang: getApiLangParam(i18n.language), ...payload },
            secSession.pk,
          ),
          onCreateBookingSuccess,
          onError,
        );

      const onSecSession = async (secSession: RequestSecSessionRes) => {
        if (secSession.sign && process.env.REACT_APP_SERVER_PUBLIC_CERT) {
          const isValid = await verifySignatureWithPKCS1(
            process.env.REACT_APP_SERVER_PUBLIC_CERT,
            secSession.pk,
            secSession.sign,
          );
          if (!isValid) {
            setError({
              message: i18n.t("securityWarningMessage"),
              confirmTitle: i18n.t("reload"),
              onConfirm: () => navigate(0),
            });
            console.error("Signature is invalid!");
            return;
          }
        }
        onCreateBookingEnc(secSession);
      };

      !secSession
        ? requestSecSession(
            { k: generateRandomString() },
            async (secSession) => {
              setSecSession(secSession);
              onSecSession(secSession);
            },
            () => setSecSession(null),
          )
        : onCreateBookingEnc(secSession);
    } else {
      setSecSession(null);
      createBooking(payload, onCreateBookingSuccess, onError);
    }
  };

  const verifyInternalUserAndSubmit = (
    internalAuthValues: InternalBookingAuthValues,
    onInvalid: () => void,
    onError: () => void,
  ) => {
    const { countryCode, mobileNo } = formValues;
    if (!countryCode || !mobileNo) {
      onError();
      setError({
        message: i18n.t("systemErrorDetail"),
      });
      return;
    }

    verifyInternalUser(
      {
        countryCode,
        mobileNo,
        accountName: internalAuthValues.accountName,
        password: internalAuthValues.password,
      },
      ({ sid, isSuccess }) => {
        if (!isSuccess) {
          onInvalid();
          return;
        }

        submit(sid, onError);
      },
      onError,
    );
  };

  const init = () => {
    setPage(PAGE.FORM);
    setFormValues(defaultValues);
    setActiveFormSection(FORM_SECTION.DEPARTMENT);
    setBookingOnLoadData(undefined);
    setBookingOnLoadDataError(false);
    setBookingTimeLabel(undefined);
    cmsSiteId &&
      bookingOnLoad(
        { cmsSiteId },
        setBookingOnLoadData,
        handleBookingOnLoadError,
      );
  };

  const handleBookingOnLoadError = () => {
    setBookingOnLoadDataError(true);
  };

  useEffect(() => {
    if (
      cmsSiteId !== null &&
      cmsSiteId !== undefined &&
      cmsSiteId !== prevCmsSiteIdRef.current
    ) {
      prevCmsSiteIdRef.current = cmsSiteId;
      setBookingOnLoadData(undefined);
      setBookingOnLoadDataError(false);
      bookingOnLoad(
        { cmsSiteId },
        setBookingOnLoadData,
        handleBookingOnLoadError,
      );
    }
  }, [cmsSiteId]);

  useEffect(() => {
    // window.scrollTo(0, 0);
    // For ios safari scrollTo bug
    setTimeout(() => window.scroll({ top: -1, left: 0 }), 10);

    // Prevent keyframe animation happen at the start
    document.body.className = "preload";
    const timer = setTimeout(
      () => document.body.classList.remove("preload"),
      300,
    );

    return () => clearTimeout(timer);
  }, [page]);

  useEffect(() => {
    const mobileNoElement = document.getElementsByName("mobileNo")[0] as
      | HTMLInputElement
      | undefined;
    if (scrollToMobileNo && mobileNoElement) {
      mobileNoElement.focus({ preventScroll: true });
      setTimeout(() => {
        window.scrollTo({
          top:
            mobileNoElement.getBoundingClientRect().top + window.scrollY - 46,
          behavior: "smooth",
        });
        setPatientFormNoScroll(false);
      }, 750);
      setScrollToMobileNo(false);
    }
  }, [document.getElementsByName("mobileNo")[0], scrollToMobileNo]);

  useEffect(() => {
    const handleBeforeUnload = () => window.scrollTo(0, 0);
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, []);

  useEffect(() => {
    moment.tz.setDefault("Asia/Hong_Kong");
  }, []);

  return (
    <Layout
      isFormValuesDirty={isFormValuesDirty}
      backgroundVariant={isInternalBooking ? "internal" : "default"}
    >
      {systemMessage && !showBookingAvailabilityNotice ? (
        <Disable systemMessage={systemMessage} />
      ) : bookingOnLoadDataError ? (
        <ErrorBoundary />
      ) : (
        <>
          {page === PAGE.FORM && (
            <>
              {systemMessage && showBookingAvailabilityNotice && (
                <BookingAvailabilityNotice message={systemMessage} />
              )}
              <Form
                flow={flow}
                departmentOptions={departmentOptions}
                careProviderOptions={careProviderOptions}
                serviceItemOptions={
                  formValues.departmentValue && flow === FORM_FLOW.DOCTOR_LAST
                    ? serviceItemsWithProviders
                    : serviceItemOptions
                }
                formValues={formValues}
                onChangeFormValues={setFormValues}
                activeFormSection={activeFormSection}
                onSubmit={() => setPage(PAGE.PREVIEW)}
                patientFormNoScroll={patientFormNoScroll}
                bookingTimeLabel={bookingTimeLabel}
                onSelectTime={setBookingTimeLabel}
                setIsFormValuesDirty={setIsFormValuesDirty}
              />
            </>
          )}
          {page === PAGE.PREVIEW &&
            departmentOptions &&
            careProviderOptions &&
            serviceItemOptions &&
            bookingTimeLabel && (
              <Preview
                departmentOptions={departmentOptions}
                careProviderOptions={careProviderOptions}
                serviceItemOptions={serviceItemOptions}
                bookingTimeLabel={bookingTimeLabel}
                values={formValues}
                onSubmit={(onError) => {
                  if (isInternalBooking) {
                    setPage(PAGE.INTERNAL_VALIDATION);
                    return;
                  }
                  submit(undefined, onError);
                }}
                onBack={() => {
                  setActiveFormSection(FORM_SECTION.PATIENT_FORM);
                  setPage(PAGE.FORM);
                }}
              />
            )}
          {page === PAGE.VALIDATION &&
            formValues.mobileNo &&
            formValues.countryCode && (
              <Validation
                type="create"
                onSubmit={submit}
                onBack={() => {
                  setPatientFormNoScroll(true);
                  setScrollToMobileNo(true);
                  setActiveFormSection(FORM_SECTION.PATIENT_FORM);
                  setPage(PAGE.FORM);
                }}
                mobileNo={formValues.mobileNo}
                countryCode={formValues.countryCode}
              />
            )}
          {page === PAGE.INTERNAL_VALIDATION && (
            <InternalValidation
              onSubmit={verifyInternalUserAndSubmit}
              onBack={() => {
                setActiveFormSection(FORM_SECTION.PATIENT_FORM);
                setPage(PAGE.FORM);
              }}
            />
          )}
          {page === PAGE.RESULT && result && (
            <Result
              result={result}
              onReStart={init}
              onEdit={() => {
                setActiveFormSection(FORM_SECTION.DATE_TIME);
                setPage(PAGE.FORM);
              }}
            />
          )}
        </>
      )}
    </Layout>
  );
};

export default HomePage;
